import { Button, FormDialog, Input } from "@simplicity/ui";
import { Download } from "lucide-react";
import { useRef, useState } from "react";

// eslint-disable-next-line max-lines-per-function -- SIM-1760: CSV upload dialog with file-ref state, validation feedback, and template-download. Low-churn admin tooling; the JSX layout reads top-to-bottom in one place.
export function CsvUploadDialog({
  open,
  onOpenChange,
  onUpload,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUpload: (file: File) => Promise<void>;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      await onUpload(file);
    } finally {
      setUploading(false);
    }
  };

  return (
    <FormDialog
      open={open}
      onOpenChange={(v) => {
        if (!v) {
          setFile(null);
          setProgress([]);
        }
        onOpenChange(v);
      }}
      title="Upload Users CSV"
      description={
        <>
          Upload a CSV file with columns: <strong>email</strong>, <strong>first_name</strong>,{" "}
          <strong>last_name</strong>. All users will be added as viewers.
        </>
      }
      footer={
        <>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {progress.length > 0 ? "Close" : "Cancel"}
          </Button>
          {progress.length === 0 && (
            <Button onClick={() => void handleUpload()} disabled={uploading || !file}>
              {uploading ? "Uploading..." : "Upload & Import"}
            </Button>
          )}
        </>
      }
    >
      <div className="space-y-3">
        <Button
          variant="link"
          size="sm"
          onClick={() => {
            const csv = "email,first_name,last_name\njohn@example.com,John,Doe";
            const blob = new Blob([csv], { type: "text/csv" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = "users_template.csv";
            a.click();
            URL.revokeObjectURL(url);
          }}
        >
          <Download className="h-3.5 w-3.5" /> Download CSV Template
        </Button>
        <Input
          ref={fileInputRef}
          type="file"
          accept=".csv"
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
        />
        {progress.length > 0 && (
          <div className="max-h-48 overflow-y-auto border border-border rounded p-3 text-xs space-y-1 bg-muted/30">
            {progress.map((line, idx) => (
              <div
                key={idx}
                className={
                  line.includes("Added")
                    ? "text-success"
                    : line.includes("Failed") || line.includes("Skipped")
                      ? "text-destructive"
                      : "text-muted-foreground"
                }
              >
                {line}
              </div>
            ))}
          </div>
        )}
      </div>
    </FormDialog>
  );
}
